import { alpha, type Theme } from "@mui/material";
import type { ReactNode } from "react";
import { ALPHA, INK, MOTION, NEUTRAL, SHADOW, SURFACE } from "@/theme/tokens";
import { CollapsibleSectionVariant } from "./collapsibleSectionVariant";

/** The disclosure chevron: turns over when open, reduced-motion safe. */
export const chevronSx = (open: boolean) => ({
	color: INK.MUTED,
	flexShrink: 0,
	transform: open ? "rotate(180deg)" : "rotate(0deg)",
	transition: `transform ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
	"@media (prefers-reduced-motion: reduce)": { transition: "none" },
});

const ROW_HOVER = { "&:hover": { backgroundColor: NEUTRAL[100] } } as const;

export const HEADER_SX = {
	[CollapsibleSectionVariant.Section]: { justifyContent: "space-between", width: "100%", p: 0.5, borderRadius: 1, textAlign: "left", ...ROW_HOVER },
	// A header with a glyph tile and a second line is a row you choose, so it gets a row's room and a gap before the chevron.
	rich: { justifyContent: "space-between", gap: 1.5, width: "100%", px: 1, py: 1, borderRadius: 2, textAlign: "start", ...ROW_HOVER },
	[CollapsibleSectionVariant.Inline]: { gap: 0.5, alignSelf: "flex-start", px: 0.5, py: 0.25, mx: -0.5, borderRadius: 1, textAlign: "start", ...ROW_HOVER },
	[CollapsibleSectionVariant.Eyebrow]: { gap: 0.75, width: "100%", px: 0.5, py: 0.25, mx: -0.5, borderRadius: 1, textAlign: "start", ...ROW_HOVER },
	// A card-sized row on its own surface: the ONE control that opens a page's supporting detail, so it must not read as a heading.
	[CollapsibleSectionVariant.Disclosure]: {
		justifyContent: "space-between",
		gap: 2,
		width: "100%",
		p: 2,
		borderRadius: 1,
		textAlign: "start",
		backgroundColor: SURFACE,
		border: `1px solid ${NEUTRAL[200]}`,
		boxShadow: SHADOW.PANEL_SOFT,
		"@media (hover: hover) and (pointer: fine)": { "&:hover": { boxShadow: SHADOW.PANEL_SOFT_HOVER, borderColor: NEUTRAL[300] } },
		// Open is a state of the row, not only of its chevron: the edge takes the brand while the detail shows.
		"&[aria-expanded='true']": { borderColor: (theme: Theme) => alpha(theme.palette.primary.main, ALPHA.STATUS_BORDER) },
		"&:active": { transform: `scale(${MOTION.SURFACE_PRESS_SCALE})` },
	},
} as const;

/** Only a `Section` given a glyph or a description changes shape; every other `Section` renders exactly as before. */
export const isRichSection = (variant: CollapsibleSectionVariant, icon: ReactNode | undefined, description: string | undefined): boolean =>
	variant === CollapsibleSectionVariant.Section && (icon !== undefined || description !== undefined);
